/*
 * Copyright (c) 2023. Collabro Limited
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthenticatedContext } from "./Authenticated";
import { useQuery } from "@tanstack/react-query";
import { getLocations, getPreferences, getReportData } from "../../api";
import { UserPreferences, VantageUser } from "../../interface/credentials";
import { Alert, AlertColor, PaletteMode, Snackbar } from "@mui/material";
import { ColorModeContext } from "../../App";
import { DisplayController, RosterLayer } from "RosterData";
import { DateTime, Interval } from "luxon";
import { Location } from "SearchParameters";
import LoadingScreen from "../Helper/LoadingScreen";
import { AxiosError } from "axios";
import { FilterProps } from "../Filter/FilterInput";
import { SortProps } from "../Sort/SortScreen";
import SessionDialog from "../Helper/SessionDialog";

interface AuthenticatedContextProps {
  children: any;
  loggedInUser: VantageUser;
  setLoggedInUser: any;
  setLogout: any;
}

interface FeedbackMessage {
  severity: AlertColor;
  message: string;
}

const AuthenticatedContextProvider = (props: AuthenticatedContextProps) => {
  const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);
  const [feedbackMessage, setFeedbackMessage] = useState<FeedbackMessage>({
    severity: "success",
    message: "",
  });
  const colorMode = useContext(ColorModeContext);
  const [endDate, setEndDate] = useState(DateTime.now().plus({ month: 1 }));
  const [currentLocation, setCurrentLocation] = useState<Location>({
    location: "UNK",
    locationType: "OFFS",
    description: "UNKNOWN",
  });
  const [backdropLoading, setBackdropLoading] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [displayController, setDisplayController] = useState<DisplayController>(
    {
      showPersonnelId: false,
      showJobTitle: false,
      showEmployer: false,
      showCoreCrew: false,
      showGender: false,
      showDiscipline: false,
      showTeam: false,
      showDeptCode: false,
      showTraffic: false,
      showTotals: false,
    }
  );
  const [initialFilters, setInitialFilters] = useState<FilterProps[]>([]);

  const [initialSorting, setInitialSorting] = useState<SortProps[]>([]);

  const [initialLayers, setInitialLayers] = useState<string[]>([]);
  const [interval, setInterval] = useState<DateTime[]>(
    Array.from(days(Interval.fromDateTimes(DateTime.now(), endDate)))
  );

  const [searchQuery, setSearchQuery] = useState<string>("");

  const { data: userPreferences, refetch: getLatestUserPreferences } = useQuery(
    {
      queryKey: ["preferences"],
      queryFn: getPreferences,
      refetchOnWindowFocus: false,
      // enabled: !!locationOptions,
      onSuccess: (data: UserPreferences) => {
        console.log("Setting preferences");
        colorMode.changeColorMode(data.theme.toLowerCase() as PaletteMode);
        data.preferences && setDisplayController(JSON.parse(data.preferences));
        data.filter && setInitialFilters(JSON.parse(data.filter));
        data.sort && setInitialSorting(JSON.parse(data.sort));
        data.layers && setInitialLayers(JSON.parse(data.layers));
      },
      onError: (err: AxiosError) => handleError(err),
    }
  );

  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
  };

  const sendUserFeedback = (message: FeedbackMessage) => {
    setFeedbackMessage(message);
    setFeedbackOpen(true);
  };

  useEffect(() => {
    setInterval(
      Array.from(days(Interval.fromDateTimes(DateTime.now(), endDate)))
    );
  }, [endDate]);

  const {
    data: locationOptions,
    isLoading: locationOptionsLoading,
    isFetching: locationOptionsFetching,
  } = useQuery({
    queryKey: ["locations"],
    queryFn: getLocations,
    refetchOnWindowFocus: false,
    enabled: !!userPreferences,
    onSuccess: (data: Location[]) => {
      if (data[0]) {
        if (userPreferences?.location) {
          const locationPref: Location = JSON.parse(userPreferences.location);
          const locationInScope = data.find(
            (loc: Location) => loc.location === locationPref.location
          );
          if (locationInScope) {
            setCurrentLocation(locationInScope);
          } else {
            setCurrentLocation(data[0]);
          }
        } else {
          setCurrentLocation(data[0]);
        }
      }
      if (data[0] && userPreferences?.location) {
        const locationPref: Location = JSON.parse(userPreferences.location);
        const locationInScope = data.find(
          (loc: Location) => loc.location === locationPref.location
        );
        if (locationInScope) {
          setCurrentLocation(locationInScope);
        } else {
          setCurrentLocation(data[0]);
        }
      }
    },
    onError: (err: AxiosError) => handleError(err),
  });

  const {
    error: reportDataErrorMessage,
    data: reportData,
    isLoading: reportDataLoading,
    isFetching: reportDataFetching,
    isError: reportDataError,
    refetch: getLatestReportData,
  } = useQuery(
    ["reportData", endDate, currentLocation, searchQuery],
    () =>
      getReportData(
        currentLocation!.location,
        endDate.toFormat("yyyy-LL-dd"),
        searchQuery
      ),
    {
      refetchOnWindowFocus: false,
      enabled: currentLocation.location !== "UNK",
      onError: (err: AxiosError) => handleError(err),
    }
  );

  const handleNewInterval = () => {
    setInterval(
      Array.from(days(Interval.fromDateTimes(DateTime.now(), endDate)))
    );
  };

  function* days(interval: Interval) {
    let cursor = interval.start.startOf("day");
    while (cursor < interval.end) {
      yield cursor;
      cursor = cursor.plus({ days: 1 });
    }
  }

  const handleSessionExpiry = () => {
    props.setLogout(true);
    setSessionExpired(false);
  };

  const handleError = (error: AxiosError) => {
    //Session Expired
    console.log(error);
    console.log(error.response!);
    console.log(error.response!.status);
    if (error.response!.status === 403 || error.response!.status === 405) {
      setSessionExpired(true);
    } else {
      sendUserFeedback({
        severity: "error",
        message: error.message,
      });
    }
  };

  const contextValue = useMemo(() => {
    return {
      colorMode: colorMode,
      initialFilters: initialFilters,
      setInitialFilters: setInitialFilters,
      displayController: displayController,
      setDisplayController: setDisplayController,
      initialSorting: initialSorting,
      setInitialSorting: setInitialSorting,
      getLatestUserPreferences: getLatestUserPreferences,
      getLatestReportData: getLatestReportData,
      endDate: endDate,
      setEndDate: setEndDate,
      locationOptions: locationOptions,
      interval: interval,
      handleNewInterval: handleNewInterval,
      reportDataLoading: reportDataLoading,
      reportDataFetching: reportDataFetching,
      reportDataError: reportDataError,
      reportDataErrorMessage: reportDataErrorMessage as AxiosError,
      reportData: reportData ? reportData.rosterData : null,
      totals: reportData ? reportData.totals : null,
      trafficData: reportData && reportData.traffic ? reportData.traffic : null,
      loggedInUser: props.loggedInUser,
      setLoggedInUser: props.setLoggedInUser,
      currentLocation: currentLocation,
      setCurrentLocation: setCurrentLocation,
      tableLoading: backdropLoading,
      setTableLoading: setBackdropLoading,
      sendUserFeedback: sendUserFeedback,
      handleError: handleError,
      searchQuery: searchQuery,
      setSearchQuery: setSearchQuery,
      initialLayers: initialLayers,
      setInitialLayers: setInitialLayers,
    };
  }, [
    colorMode,
    initialFilters,
    setInitialFilters,
    displayController,
    setDisplayController,
    initialSorting,
    setInitialSorting,
    getLatestUserPreferences,
    getLatestReportData,
    endDate,
    setEndDate,
    locationOptions,
    interval,
    handleNewInterval,
    reportDataLoading,
    reportDataFetching,
    reportDataError,
    reportDataErrorMessage,
    reportData,
    props.loggedInUser,
    props.setLoggedInUser,
    currentLocation,
    setCurrentLocation,
    backdropLoading,
    setBackdropLoading,
    sendUserFeedback,
    handleError,
    searchQuery,
    setSearchQuery,
    initialLayers,
    setInitialLayers,
  ]);

  if (locationOptionsLoading || locationOptionsFetching || !currentLocation)
    return <LoadingScreen />;

  return (
    <AuthenticatedContext.Provider value={contextValue}>
      <>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={feedbackOpen}
          onClose={handleFeedbackClose}
        >
          <Alert
            onClose={handleFeedbackClose}
            severity={feedbackMessage.severity}
            sx={{ width: "100%" }}
          >
            {feedbackMessage.message}
          </Alert>
        </Snackbar>
        <SessionDialog
          open={sessionExpired}
          handleClose={handleSessionExpiry}
        />
        {props.children}
      </>
    </AuthenticatedContext.Provider>
  );
};

export default AuthenticatedContextProvider;
